/**
 * CREATIVE DETONATOR - Shared Constants
 * Centralized constants used across multiple frontend modules
 *
 * @version 1.0.0
 *
 * NOTE: Category colors (15 categories A-O) are in trigger-data.js
 * Use: import { CategoryColors, getCategoryColor } from './trigger-data.js'
 */

// ============================================================================
// BRAND SHELL COLORS
// ============================================================================

/**
 * Brand cartridge shell colors
 * Maps shell color names to hex values
 */
export const SHELL_COLORS = {
  slate: '#64748b',
  orange: '#f97316',
  charcoal: '#374151',
  violet: '#8b5cf6',
  teal: '#14b8a6',
  blue: '#3b82f6',
  rose: '#f43f5e',
  amber: '#f59e0b',
  stone: '#78716c'
};

/**
 * Get shell color hex value
 * @param {string} name - Shell color name
 * @param {string} fallback - Fallback color (default: slate)
 * @returns {string} Hex color value
 */
export function getShellColor(name, fallback = '#64748b') {
  return SHELL_COLORS[name] || fallback;
}

// ============================================================================
// FORMAT DEFINITIONS
// ============================================================================

/**
 * Creative format definitions with icons
 * Used in generator, quick-launch, and dashboard
 */
export const FORMATS = [
  { id: 'social', name: 'SOCIAL MEDIA', image: '/assets/images/formats/1-social-media.png' },
  { id: 'video', name: 'VIDEO AD', image: '/assets/images/formats/2-video-ad.png' },
  { id: 'print', name: 'PRINT / OOH', image: '/assets/images/formats/3-ooh-media.png' },
  { id: 'experiential', name: 'EXPERIENTIAL', image: '/assets/images/formats/4-experimental.png' },
  { id: 'digital', name: 'DIGITAL', image: '/assets/images/formats/5-digital.png' },
  { id: 'campaign', name: 'FULL CAMPAIGN', image: '/assets/images/formats/6-full-campaign.png' }
];

/**
 * Format icon mapping for mini icons
 * Used in filter bars and compact views
 */
export const FORMAT_ICONS = {
  print: 'format-icon-mini_0002_print-ooh.png',
  video: 'format-icon-mini_0001_video.png',
  social: 'format-icon-mini_0000_social-media.png',
  digital: 'format-icon-mini_0003_digital.png',
  experiential: 'format-icon-mini_0005_experimental.png',
  campaign: 'format-icon-mini_0004_full-campaign.png'
};

/**
 * Get format by ID
 * @param {string} formatId - Format ID (e.g., 'print', 'video')
 * @returns {Object|undefined} Format object or undefined
 */
export function getFormatById(formatId) {
  return FORMATS.find(f => f.id === formatId);
}

/**
 * Get format icon filename
 * @param {string} formatId - Format ID
 * @returns {string} Icon filename
 */
export function getFormatIcon(formatId) {
  return FORMAT_ICONS[formatId] || 'format-icon-mini_0002_print-ooh.png';
}

// ============================================================================
// TIMING CONSTANTS
// ============================================================================

/**
 * Default debounce delays (ms)
 */
export const DEBOUNCE = {
  search: 300,
  resize: 150,
  scroll: 100,
  input: 200
};

/**
 * Animation durations (ms)
 */
export const ANIMATION = {
  fast: 150,
  normal: 300,
  slow: 500,
  modal: 200,
  highlightDuration: 3000,   // Deep link highlight animation
  openAfterHighlight: 1200   // Delay before opening canvas after highlight
};

/**
 * Timeout values (ms)
 */
export const TIMEOUTS = {
  dashboardReady: 10000,   // Max wait for dashboard initialization
  databaseSave: 15000,     // Database save operation timeout (increased from 5s)
  apiRequest: 30000        // General API request timeout
};

/**
 * Retry configuration
 */
export const RETRY = {
  maxAttempts: 3,
  backoffMs: 1000,         // Base delay between retries
  backoffMultiplier: 2     // Exponential backoff multiplier
};

// ============================================================================
// PAGINATION DEFAULTS
// ============================================================================

export const PAGINATION = {
  defaultItemsPerPage: 12,
  itemsPerPageOptions: [6, 12, 24, 48]
};

// ============================================================================
// WINDOW EXPORTS (for non-module usage)
// ============================================================================

if (typeof window !== 'undefined') {
  window.SHELL_COLORS = SHELL_COLORS;
  window.FORMATS = FORMATS;
  window.FORMAT_ICONS = FORMAT_ICONS;
  window.getShellColor = getShellColor;
  window.getFormatById = getFormatById;
  window.getFormatIcon = getFormatIcon;
}

export default {
  SHELL_COLORS,
  FORMATS,
  FORMAT_ICONS,
  DEBOUNCE,
  ANIMATION,
  TIMEOUTS,
  RETRY,
  PAGINATION,
  getShellColor,
  getFormatById,
  getFormatIcon
};
